import { Link } from "@inertiajs/react"
import { ArrowRight, Wallet } from "lucide-react"
import type { RefObject } from "react"
import { SpendLimitRing } from "@/components/billing/spend-limit-ring"
import { ShellMenu } from "@/components/shell/shell-menu"
import type { DropdownMenuDirection } from "@/components/ui/dropdown-menu-position"
import { ShareMeter } from "@/components/ui/share-meter"
import { useSpendLimitText } from "@/hooks/use-spend-limit-text"
import { useTranslation } from "@/hooks/use-translation"
import { formatRubWhole } from "@/lib/billing-format"
import { spendLimitPercent, spendLimitRemaining, spendLimitTone } from "@/lib/spend-limit"
import { cn } from "@/lib/utils"
import type { SpendLimitShare } from "@/types/billing"

const POPOVER_WIDTH = 372

const FOCUS_RING = "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"

const RING_BADGE_CLASS = "flex size-5 shrink-0 items-center justify-center rounded-md transition-colors"

const ringTrackClass = (open: boolean) =>
  cn("group-hover/limit:stroke-ui-border-strong", open && "stroke-ui-border-strong")

export function useSpendLimitTooltip(share: SpendLimitShare | null): string | null {
  const { t } = useTranslation("layout")
  const text = useSpendLimitText()
  if (!share) return null
  return t("shell.spendLimit.tooltip", {
    left: formatRubWhole(spendLimitRemaining(share.spent_microcents, share.limit_microcents)),
    resets: text.resetsAtLower(share.resets_at),
  })
}

function useSpendLimitNav(share: SpendLimitShare) {
  const tooltip = useSpendLimitTooltip(share) ?? ""
  const percent = spendLimitPercent(share.spent_microcents, share.limit_microcents) ?? 0
  const left = spendLimitRemaining(share.spent_microcents, share.limit_microcents)
  return { percent, left, tooltip }
}

type SpendLimitPopoverProps = {
  share: SpendLimitShare
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLElement | null>
  rootRef: RefObject<HTMLElement | null>
  direction?: DropdownMenuDirection
  onNavigate: () => void
}

/** Подробности по клику (docs/billing_limits.md §9): строка месяца и полоса, без состояния. */
export function SpendLimitPopover({
  share,
  open,
  onClose,
  anchorRef,
  rootRef,
  direction = "down",
  onNavigate,
}: SpendLimitPopoverProps) {
  const { t } = useTranslation("layout")
  const text = useSpendLimitText()
  const { percent } = useSpendLimitNav(share)
  const title = t("shell.spendLimit.title")
  const headerClass = "flex items-center justify-between gap-2 px-3.5 pt-1.5 pb-1 text-xs text-ui-text-muted"

  return (
    <ShellMenu
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      rootRef={rootRef}
      width={POPOVER_WIDTH}
      direction={direction}
      align="left"
      ariaLabel={title}
    >
      {share.href ? (
        <Link
          href={share.href}
          role="menuitem"
          data-shell-menu-item
          onClick={() => {
            onClose()
            onNavigate()
          }}
          className={cn(
            headerClass,
            "transition-colors outline-none hover:text-ui-text-primary focus-visible:text-ui-text-primary",
          )}
        >
          <span>{title}</span>
          <ArrowRight className="size-3.5 shrink-0" aria-hidden />
        </Link>
      ) : (
        <div className={headerClass}>{title}</div>
      )}
      <div className="px-3.5 pt-2 pb-1.5">
        <div className="flex items-baseline gap-2.5">
          <span className="min-w-0 flex-1 truncate text-[13px] leading-[18px] text-ui-text-primary tabular-nums">
            {t("shell.spendLimit.month", { limit: formatRubWhole(share.limit_microcents) })}
          </span>
          <span className="shrink-0 text-xs whitespace-nowrap text-ui-text-muted tabular-nums">
            {text.resetsAt(share.resets_at)}
          </span>
          <span className="min-w-[34px] shrink-0 text-right text-xs text-ui-text-muted tabular-nums">
            {text.percent(percent)}
          </span>
        </div>
        <ShareMeter
          value={percent}
          max={1}
          tone={spendLimitTone(percent)}
          className="mt-1.5 flex h-1 w-full bg-ui-range-track"
        />
        {share.topup_microcents > 0 ? (
          <p className="mt-1.5 text-[11px] leading-4 text-ui-text-muted tabular-nums">
            {t("shell.spendLimit.topup", { amount: formatRubWhole(share.topup_microcents) })}
          </p>
        ) : null}
      </div>
    </ShellMenu>
  )
}

type SpendLimitTriggerProps = {
  share: SpendLimitShare
  open: boolean
  onToggle: () => void
}

/** Кольцо во второй строке владельца: «кошелёк · баланс · кольцо · +». */
export function SpendLimitRingButton({ share, open, onToggle }: SpendLimitTriggerProps) {
  const nav = useSpendLimitNav(share)
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={nav.tooltip}
      title={nav.tooltip}
      onClick={onToggle}
      className={cn(
        RING_BADGE_CLASS,
        "group/limit pointer-events-auto hover:bg-ui-surface-hover",
        FOCUS_RING,
        open && "bg-ui-surface-hover",
      )}
    >
      <SpendLimitRing percent={nav.percent} size={12} trackClassName={ringTrackClass(open)} />
    </button>
  )
}

/** Вторая строка сотрудника: «кошелёк · остаток · кольцо». До md зона нажатия — вся строка, с md — только кольцо. */
export function SpendLimitRow({ share, open, onToggle }: SpendLimitTriggerProps) {
  const nav = useSpendLimitNav(share)
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={nav.tooltip}
      title={nav.tooltip}
      onClick={onToggle}
      className={cn(
        "group/limit pointer-events-auto -ml-1 flex h-5 max-w-full min-w-0 items-center gap-1 rounded-md pl-1 transition-colors md:pointer-events-none md:focus-visible:ring-0",
        FOCUS_RING,
        open && "max-md:bg-ui-surface-hover",
      )}
    >
      <Wallet className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate text-[var(--ui-nav-idle-fg)] tabular-nums">{formatRubWhole(nav.left)}</span>
      <span
        className={cn(
          RING_BADGE_CLASS,
          "group-hover/limit:bg-ui-surface-hover md:pointer-events-auto",
          "md:group-focus-visible/limit:ring-2 md:group-focus-visible/limit:ring-[var(--ui-focus-ring)]",
          open && "md:bg-ui-surface-hover",
        )}
      >
        <SpendLimitRing percent={nav.percent} size={12} trackClassName={ringTrackClass(open)} />
      </span>
    </button>
  )
}

/** Кольцо в углу логотипа свёрнутого рельса (docs/billing_limits.md §9). */
export function SpendLimitRailBadge({ share, open, onToggle }: SpendLimitTriggerProps) {
  const nav = useSpendLimitNav(share)
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={nav.tooltip}
      onClick={onToggle}
      className={cn(
        "group/limit absolute -right-[5px] -bottom-[5px] flex size-[18px] items-center justify-center rounded-full bg-ui-bg-elevated",
        FOCUS_RING,
      )}
    >
      <SpendLimitRing percent={nav.percent} size={12} trackClassName={ringTrackClass(open)} />
    </button>
  )
}
